import { useCallback, useEffect, useState } from "react";
import {
  ShieldCheck, CheckCircle2, XCircle, Loader2, IdCard, Smartphone, UserCheck,
  Link2, Trash2, RefreshCw, Search, Unlink, AlertTriangle, Copy, Users,
} from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";

const BRAND = "#002FA7";
const card = "rounded-md border border-[#E5E7EB] bg-white";
const label = "text-[11px] uppercase tracking-[0.12em] text-[#6B7280]";

function StatusPill({ ok, children, testid }) {
  return (
    <span data-testid={testid}
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${
        ok ? "bg-[#E7F5EA] text-[#2E7D32]" : "bg-[#FFF4E6] text-[#B45309]"}`}>
      {ok ? <CheckCircle2 className="h-3.5 w-3.5" /> : <AlertTriangle className="h-3.5 w-3.5" />}
      {children}
    </span>
  );
}

function Field({ label: lbl, hint, children }) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-[#374151]">{lbl}</span>
      {children}
      {hint && <span className="block mt-1 text-[11px] text-[#6B7280]">{hint}</span>}
    </label>
  );
}

function ConsentBox({ checked, onChange, text }) {
  return (
    <div className={`${card} p-3 bg-[#F9FAFB]`}>
      <p className={label}>ABDM Consent (v1.4)</p>
      <div className="relative mt-2">
        <pre className="whitespace-pre-wrap text-[12px] leading-5 text-[#374151] font-body max-h-48 overflow-y-auto pb-4"
          data-testid="abdm-consent-text">{text || "Loading consent language…"}</pre>
        <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-6 bg-gradient-to-t from-[#F9FAFB] to-transparent" />
      </div>
      <p className="mt-1 text-[11px] text-[#6B7280]">Scroll to read the full consent text.</p>
      <label className="mt-3 flex items-start gap-2 cursor-pointer">
        <Checkbox checked={checked} onCheckedChange={onChange} data-testid="abdm-consent-checkbox"
          className="mt-0.5" />
        <span className="text-[12px] text-[#111827] font-medium">
          I have read the consent to the patient and they agree to proceed.
        </span>
      </label>
    </div>
  );
}

function SuccessCard({ profile, onReset, onGoLinks }) {
  const copy = (v) => { navigator.clipboard?.writeText(v); toast.success("Copied"); };
  return (
    <div className={`${card} p-5`} data-testid="abdm-success-card">
      <div className="flex items-center gap-2 text-[#2E7D32]">
        <CheckCircle2 className="h-5 w-5" />
        <p className="font-heading font-bold">ABHA ready</p>
      </div>
      <div className="mt-4 grid sm:grid-cols-2 gap-4">
        <div className="flex items-start gap-3">
          {profile.photo
            ? <img alt="ABHA" src={`data:image/jpeg;base64,${profile.photo}`}
                className="h-16 w-16 rounded-md object-cover border border-[#E5E7EB]" />
            : <div className="h-16 w-16 rounded-md bg-[#F3F4F6] flex items-center justify-center">
                <IdCard className="h-7 w-7 text-[#9CA3AF]" /></div>}
          <div>
            <p className="font-heading font-bold text-[#111827]" data-testid="abdm-success-name">
              {profile.name || "—"}</p>
            <p className="text-xs text-[#6B7280]">{profile.gender} · {profile.dob || "—"}</p>
            {profile.kyc_verified && <StatusPill ok testid="abdm-kyc-pill">KYC verified</StatusPill>}
          </div>
        </div>
        <div className="space-y-2">
          <div>
            <p className={label}>ABHA Number</p>
            <button onClick={() => copy(profile.abha_number)} data-testid="abdm-success-number"
              className="font-mono text-[#002FA7] font-semibold flex items-center gap-1.5 hover:underline">
              {profile.abha_number || "—"} <Copy className="h-3 w-3" /></button>
          </div>
          <div>
            <p className={label}>ABHA Address</p>
            <p className="font-mono text-[#111827]" data-testid="abdm-success-address">
              {profile.abha_address || "— (set below)"}</p>
          </div>
        </div>
      </div>
      <div className="mt-4 flex gap-2">
        <Button onClick={onGoLinks} data-testid="abdm-success-view-links"
          className="bg-[#002FA7] hover:bg-[#00237C] text-white">
          <Users className="h-4 w-4 mr-2" /> View linked patients</Button>
        <Button variant="outline" onClick={onReset} data-testid="abdm-success-reset">Start another</Button>
      </div>
    </div>
  );
}

/* ---------------- Create ABHA via Aadhaar OTP ---------------- */
function CreateAbha({ consentText, onDone }) {
  const [step, setStep] = useState("form"); // form -> otp -> address -> done
  const [aadhaar, setAadhaar] = useState("");
  const [consent, setConsent] = useState(false);
  const [txnId, setTxnId] = useState("");
  const [otp, setOtp] = useState("");
  const [mobile, setMobile] = useState("");
  const [busy, setBusy] = useState(false);
  const [profile, setProfile] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [addr, setAddr] = useState("");

  const requestOtp = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/abdm/create/aadhaar/request-otp", { aadhaar });
      setTxnId(data.txnId); setStep("otp");
      toast.success(data.message || "OTP sent to Aadhaar-linked mobile");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const resend = async () => {
    try {
      const { data } = await api.post("/abdm/create/aadhaar/resend-otp", { txnId, aadhaar });
      toast.success(data.message || "OTP resent");
    } catch (e) { toast.error(errMsg(e)); }
  };
  const verify = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/abdm/create/aadhaar/verify",
        { txnId, otp, mobile: mobile || undefined, consent });
      setProfile(data.profile);
      toast.success("ABHA created");
      try {
        const s = await api.get("/abdm/create/address-suggestions", { params: { txnId: data.txnId } });
        setSuggestions(s.data.suggestions || []);
      } catch { /* address suggestions optional */ }
      setStep("address");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const saveAddress = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/abdm/create/address",
        { txnId, preferredAbhaAddress: addr });
      setProfile((p) => ({ ...p, abha_address: data.abha_address }));
      setStep("done"); onDone?.();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const reset = () => {
    setStep("form"); setAadhaar(""); setConsent(false); setTxnId(""); setOtp("");
    setMobile(""); setProfile(null); setSuggestions([]); setAddr("");
  };

  if (step === "done") return <SuccessCard profile={profile} onReset={reset} onGoLinks={onDone} />;

  return (
    <div className="space-y-4" data-testid="abdm-create-panel">
      {step === "form" && (
        <div className="space-y-4">
          <ConsentBox checked={consent} onChange={setConsent} text={consentText} />
          <Field lbl="Aadhaar Number" hint="12 digits. Used once for UIDAI OTP — never stored by LabTrack.">
            <Input value={aadhaar} data-testid="abdm-aadhaar-input" inputMode="numeric" maxLength={12}
              onChange={(e) => setAadhaar(e.target.value.replace(/\D/g, ""))}
              placeholder="XXXX XXXX XXXX" className="mt-1 font-mono tracking-widest" />
          </Field>
          <Button disabled={!consent || aadhaar.length !== 12 || busy} onClick={requestOtp}
            data-testid="abdm-request-otp-btn" className="bg-[#002FA7] hover:bg-[#00237C] text-white">
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Smartphone className="h-4 w-4 mr-2" />}
            Send Aadhaar OTP
          </Button>
        </div>
      )}

      {step === "otp" && (
        <div className="space-y-4">
          <p className="text-sm text-[#374151]">
            Enter the 6-digit OTP sent to the Aadhaar-linked mobile number.</p>
          <Field lbl="Aadhaar OTP">
            <Input value={otp} data-testid="abdm-otp-input" inputMode="numeric" maxLength={6}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
              placeholder="••••••" className="mt-1 font-mono tracking-[0.5em] text-lg" />
          </Field>
          <Field lbl="Communication mobile (optional)"
            hint="If different from the Aadhaar-linked number, an extra OTP verifies it.">
            <Input value={mobile} data-testid="abdm-comm-mobile-input" inputMode="numeric" maxLength={10}
              onChange={(e) => setMobile(e.target.value.replace(/\D/g, ""))}
              placeholder="10-digit mobile" className="mt-1 font-mono" />
          </Field>
          <div className="flex gap-2">
            <Button disabled={otp.length !== 6 || busy} onClick={verify} data-testid="abdm-verify-btn"
              className="bg-[#002FA7] hover:bg-[#00237C] text-white">
              {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ShieldCheck className="h-4 w-4 mr-2" />}
              Verify & create ABHA</Button>
            <Button variant="outline" onClick={resend} data-testid="abdm-resend-btn">
              <RefreshCw className="h-4 w-4 mr-2" /> Resend OTP</Button>
          </div>
        </div>
      )}

      {step === "address" && (
        <div className="space-y-4">
          {profile && <SuccessCard profile={profile} onReset={reset} onGoLinks={onDone} />}
          <div className={`${card} p-4`}>
            <p className="font-heading font-semibold text-[#111827]">Choose an ABHA address</p>
            <p className="text-xs text-[#6B7280] mt-0.5">Pick a suggestion or type a preferred one.</p>
            {suggestions.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2" data-testid="abdm-address-suggestions">
                {suggestions.map((s) => (
                  <button key={s} onClick={() => setAddr(s)} data-testid={`abdm-suggestion-${s}`}
                    className={`px-3 py-1.5 rounded-full border text-sm font-mono ${
                      addr === s ? "border-[#002FA7] bg-[#EEF2FF] text-[#002FA7]"
                        : "border-[#E5E7EB] text-[#374151] hover:border-[#002FA7]"}`}>{s}</button>
                ))}
              </div>
            )}
            <Input value={addr} data-testid="abdm-address-input" className="mt-3 font-mono"
              onChange={(e) => setAddr(e.target.value)} placeholder="preferred.name" />
            <div className="flex gap-2 mt-3">
              <Button disabled={!addr || busy} onClick={saveAddress} data-testid="abdm-save-address-btn"
                className="bg-[#002FA7] hover:bg-[#00237C] text-white">Save ABHA address</Button>
              <Button variant="outline" onClick={() => { setStep("done"); onDone?.(); }}
                data-testid="abdm-skip-address-btn">Skip for now</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------------- Verify / link existing ABHA ---------------- */
const METHODS = [
  { key: "aadhaar", label: "Aadhaar OTP", icon: IdCard, ph: "12-digit Aadhaar", len: 12 },
  { key: "abha-number", label: "ABHA Number OTP", icon: UserCheck, ph: "14-digit ABHA number", len: 14 },
  { key: "mobile", label: "Mobile OTP", icon: Smartphone, ph: "10-digit mobile", len: 10 },
];

function VerifyAbha({ consentText, onDone }) {
  const [method, setMethod] = useState("aadhaar");
  const [value, setValue] = useState("");
  const [consent, setConsent] = useState(false);
  const [step, setStep] = useState("form"); // form -> otp -> accounts -> done
  const [txnId, setTxnId] = useState("");
  const [otp, setOtp] = useState("");
  const [accounts, setAccounts] = useState([]);
  const [profile, setProfile] = useState(null);
  const [busy, setBusy] = useState(false);
  const cfg = METHODS.find((m) => m.key === method);

  const reset = () => {
    setStep("form"); setValue(""); setConsent(false); setTxnId(""); setOtp("");
    setAccounts([]); setProfile(null);
  };
  const requestOtp = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/abdm/verify/request-otp", { method, value, consent });
      setTxnId(data.txnId); setStep("otp");
      toast.success(data.message || "OTP sent");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const confirm = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/abdm/verify/confirm-otp", { txnId, otp, method });
      if (data.accounts?.length) { setAccounts(data.accounts); setStep("accounts"); }
      else { setProfile(data.profile); setStep("done"); onDone?.(); }
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const pick = async (abhaNumber) => {
    setBusy(true);
    try {
      const { data } = await api.post("/abdm/verify/select-account", { txnId, abhaNumber });
      setProfile(data.profile); setStep("done"); onDone?.();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  if (step === "done") return <SuccessCard profile={profile} onReset={reset} onGoLinks={onDone} />;

  return (
    <div className="space-y-4" data-testid="abdm-verify-panel">
      {step === "form" && (
        <>
          <div className="flex flex-wrap gap-2" data-testid="abdm-method-tabs">
            {METHODS.map((m) => {
              const Icon = m.icon;
              return (
                <button key={m.key} onClick={() => { setMethod(m.key); setValue(""); }}
                  data-testid={`abdm-method-${m.key}`}
                  className={`flex items-center gap-2 px-3 py-2 rounded-md border text-sm transition-colors ${
                    method === m.key ? "border-[#002FA7] bg-[#EEF2FF] text-[#002FA7] font-medium"
                      : "border-[#E5E7EB] text-[#374151] hover:border-[#002FA7]"}`}>
                  <Icon className="h-4 w-4" /> {m.label}
                </button>
              );
            })}
          </div>
          <ConsentBox checked={consent} onChange={setConsent} text={consentText} />
          <Field lbl={cfg.label} hint="An OTP will be sent to the registered number.">
            <Input value={value} data-testid="abdm-verify-input" inputMode="numeric" maxLength={cfg.len}
              onChange={(e) => setValue(e.target.value.replace(/\D/g, ""))}
              placeholder={cfg.ph} className="mt-1 font-mono tracking-wider" />
          </Field>
          <Button disabled={!consent || value.length < 10 || busy} onClick={requestOtp}
            data-testid="abdm-verify-request-btn" className="bg-[#002FA7] hover:bg-[#00237C] text-white">
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Smartphone className="h-4 w-4 mr-2" />}
            Send OTP</Button>
        </>
      )}

      {step === "otp" && (
        <div className="space-y-4">
          <p className="text-sm text-[#374151]">Enter the 6-digit OTP.</p>
          <Field lbl="OTP">
            <Input value={otp} data-testid="abdm-verify-otp-input" inputMode="numeric" maxLength={6}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
              placeholder="••••••" className="mt-1 font-mono tracking-[0.5em] text-lg" />
          </Field>
          <Button disabled={otp.length !== 6 || busy} onClick={confirm} data-testid="abdm-verify-confirm-btn"
            className="bg-[#002FA7] hover:bg-[#00237C] text-white">
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ShieldCheck className="h-4 w-4 mr-2" />}
            Verify & link</Button>
        </div>
      )}

      {step === "accounts" && (
        <div className="space-y-3" data-testid="abdm-accounts-list">
          <p className="text-sm text-[#374151]">Multiple ABHA accounts found. Select one:</p>
          {accounts.map((a) => (
            <button key={a.abha_number} onClick={() => pick(a.abha_number)} disabled={busy}
              data-testid={`abdm-account-${a.abha_number}`}
              className={`${card} w-full text-left p-3 hover:border-[#002FA7] flex items-center justify-between`}>
              <span><span className="font-mono text-[#002FA7]">{a.abha_number}</span>
                <span className="ml-2 text-[#111827]">{a.name}</span></span>
              <Link2 className="h-4 w-4 text-[#6B7280]" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------------- Linked patients table ---------------- */
function LinkedPatients({ reloadKey }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [attach, setAttach] = useState(null);
  const [ulid, setUlid] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/abdm/links", { params: { search } });
      setRows(data.links || []);
    } catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  }, [search]);
  useEffect(() => { load(); }, [load, reloadKey]);

  const doAttach = async () => {
    if (!ulid.trim()) return toast.error("Enter a patient ULID");
    try {
      await api.post(`/abdm/links/${attach.link_id}/attach`, { ulid: ulid.trim() });
      toast.success("ABHA linked to patient"); setAttach(null); setUlid(""); load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const detach = async (id) => {
    try { await api.post(`/abdm/links/${id}/detach`); toast.success("Unlinked"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Remove this ABHA record from LabTrack?")) return;
    try { await api.delete(`/abdm/links/${id}`); toast.success("Removed"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const TH = "px-3 py-2 text-left text-[10px] uppercase tracking-[0.1em] text-[#6B7280]";
  return (
    <div>
      <form className="flex gap-2 mb-4" onSubmit={(e) => { e.preventDefault(); load(); }}>
        <div className="flex items-center gap-2 flex-1 rounded-md border border-[#E5E7EB] bg-white px-3">
          <Search className="h-4 w-4 text-[#6B7280]" />
          <input value={search} onChange={(e) => setSearch(e.target.value)}
            data-testid="abdm-links-search" placeholder="Search ABHA number, address, name or ULID…"
            className="flex-1 h-10 text-sm outline-none bg-transparent" />
        </div>
        <Button type="submit" data-testid="abdm-links-search-btn"
          className="bg-[#002FA7] hover:bg-[#00237C] text-white">Search</Button>
      </form>

      <div className={`${card} overflow-hidden`}>
        <div className="px-3 py-2 border-b border-[#E5E7EB] flex items-center gap-2">
          <IdCard className="h-4 w-4 text-[#002FA7]" />
          <p className="text-xs text-[#6B7280]" data-testid="abdm-links-count">
            {loading ? "Loading…" : `${rows.length} ABHA record(s)`}</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F9FAFB]">
              <tr>{["ABHA Number", "ABHA Address", "Name", "Status", "Linked ULID", ""].map((h) =>
                <th key={h} className={TH}>{h}</th>)}</tr>
            </thead>
            <tbody>
              {!loading && rows.length === 0 && (
                <tr><td colSpan={6} className="px-3 py-8 text-center text-sm text-[#6B7280]"
                  data-testid="abdm-links-empty">
                  No ABHA records yet. Create or verify an ABHA to see it here.</td></tr>
              )}
              {rows.map((p) => (
                <tr key={p.link_id} className="border-t border-[#E5E7EB] hover:bg-gray-50"
                  data-testid={`abdm-link-row-${p.link_id}`}>
                  <td className="px-3 py-2 font-mono text-[#002FA7]">{p.abha_number || "—"}</td>
                  <td className="px-3 py-2 font-mono text-[#111827]">{p.abha_address || "—"}</td>
                  <td className="px-3 py-2 text-[#111827]">{p.name || "—"}</td>
                  <td className="px-3 py-2">
                    <StatusPill ok={p.status === "created" || p.status === "verified"}
                      testid={`abdm-status-${p.link_id}`}>
                      {p.status === "created" ? "Created" : p.status === "verified" ? "Verified" : p.status}
                    </StatusPill>
                  </td>
                  <td className="px-3 py-2 font-mono">
                    {p.patient_ulid
                      ? <span className="text-[#2E7D32]">{p.patient_ulid}</span>
                      : <span className="text-[#9CA3AF]">not linked</span>}
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    {p.patient_ulid
                      ? <button onClick={() => detach(p.link_id)} title="Unlink from patient"
                          data-testid={`abdm-detach-${p.link_id}`}
                          className="p-1.5 rounded hover:bg-[#F3F4F6] text-[#B45309]">
                          <Unlink className="h-4 w-4" /></button>
                      : <button onClick={() => { setAttach(p); setUlid(""); }} title="Link to a patient ULID"
                          data-testid={`abdm-attach-${p.link_id}`}
                          className="p-1.5 rounded hover:bg-[#F3F4F6] text-[#002FA7]">
                          <Link2 className="h-4 w-4" /></button>}
                    {isAdmin && (
                      <button onClick={() => remove(p.link_id)} title="Remove record"
                        data-testid={`abdm-delete-${p.link_id}`}
                        className="p-1.5 rounded hover:bg-[#F3F4F6] text-[#C92A2A]">
                        <Trash2 className="h-4 w-4" /></button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {attach && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
          data-testid="abdm-attach-overlay" onClick={() => setAttach(null)}>
          <div className="bg-white rounded-lg p-4 w-full max-w-sm" onClick={(e) => e.stopPropagation()}>
            <p className="font-heading font-bold text-sm text-[#111827]">Link ABHA to a patient</p>
            <p className="text-xs text-[#6B7280] mt-0.5 font-mono">{attach.abha_number}</p>
            <Field lbl="Patient ULID">
              <Input value={ulid} className="mt-1 font-mono" data-testid="abdm-attach-ulid-input"
                onChange={(e) => setUlid(e.target.value)} placeholder="LTBP-…" />
            </Field>
            <div className="flex justify-end gap-2 mt-3">
              <Button variant="outline" size="sm" onClick={() => setAttach(null)}
                data-testid="abdm-attach-cancel">Cancel</Button>
              <Button size="sm" onClick={doAttach} data-testid="abdm-attach-save"
                className="bg-[#002FA7] hover:bg-[#00237C] text-white">Link</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------------- Page ---------------- */
const M1_CHECKLIST = [
  "Create ABHA via Aadhaar OTP (with consent)",
  "Verify existing ABHA (Aadhaar / ABHA number / Mobile OTP)",
  "Collect & display mandatory ABDM consent",
  "Store ABHA number + ABHA address; link to patient ULID",
  "Aadhaar RSA-encrypted in transit and never stored",
];

export default function Abdm() {
  const [status, setStatus] = useState(null);
  const [consentText, setConsentText] = useState("");
  const [tab, setTab] = useState("overview");
  const [reloadKey, setReloadKey] = useState(0);

  const loadStatus = useCallback(async () => {
    try { const { data } = await api.get("/abdm/status"); setStatus(data); }
    catch (e) { toast.error(errMsg(e)); }
  }, []);
  useEffect(() => {
    loadStatus();
    api.get("/abdm/consent-text").then(({ data }) => setConsentText(data.text)).catch(() => {});
  }, [loadStatus]);

  const afterAction = () => { setReloadKey((k) => k + 1); setTab("links"); };

  return (
    <div data-testid="abdm-page">
      <div className="flex items-start gap-3">
        <div className="h-11 w-11 rounded-md bg-[#EEF2FF] flex items-center justify-center shrink-0">
          <ShieldCheck className="h-6 w-6 text-[#002FA7]" />
        </div>
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight text-[#111827]">
            ABDM Integration <span className="text-[#6B7280] font-semibold text-lg">· Milestone 1</span>
          </h1>
          <p className="text-sm text-[#6B7280] mt-1">
            Link LabTrack to the Ayushman Bharat Digital Mission — create & verify ABHA (health accounts)
            and attach them to your patients.
          </p>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="mt-6">
        <div className="max-w-full overflow-x-auto">
        <TabsList data-testid="abdm-tabs">
          <TabsTrigger value="overview" data-testid="abdm-tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="create" data-testid="abdm-tab-create">Create ABHA</TabsTrigger>
          <TabsTrigger value="verify" data-testid="abdm-tab-verify">Verify / Link existing</TabsTrigger>
          <TabsTrigger value="links" data-testid="abdm-tab-links">Linked patients</TabsTrigger>
        </TabsList>
        </div>

        <TabsContent value="overview" className="mt-4">
          <div className="grid lg:grid-cols-2 gap-4">
            <div className={`${card} p-5`}>
              <div className="flex items-center justify-between">
                <p className="font-heading font-bold text-[#111827]">Connection</p>
                <button onClick={loadStatus} data-testid="abdm-refresh-status"
                  className="p-1.5 rounded hover:bg-[#F3F4F6] text-[#6B7280]">
                  <RefreshCw className="h-4 w-4" /></button>
              </div>
              {!status ? <p className="text-sm text-[#6B7280] mt-3">Checking…</p> : (
                <div className="mt-3 space-y-3">
                  <div className="flex items-center gap-2">
                    {status.connected
                      ? <StatusPill ok testid="abdm-conn-pill">Connected to sandbox</StatusPill>
                      : status.configured
                        ? <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium bg-[#FDECEC] text-[#C92A2A]" data-testid="abdm-conn-pill">
                            <XCircle className="h-3.5 w-3.5" /> Not reachable</span>
                        : <StatusPill ok={false} testid="abdm-conn-pill">Credentials pending</StatusPill>}
                  </div>
                  <p className="text-sm text-[#374151]" data-testid="abdm-status-message">{status.message}</p>
                  <dl className="grid grid-cols-2 gap-y-2 text-sm pt-2 border-t border-[#E5E7EB]">
                    <dt className={label}>Environment</dt>
                    <dd className="text-[#111827]">{status.env}</dd>
                    <dt className={label}>Credentials</dt>
                    <dd className="text-[#111827]">{status.configured ? "Configured" : "Not set"}</dd>
                    <dt className={label}>HIP / HFR ID</dt>
                    <dd className="text-[#111827] font-mono">{status.hip_id || "—"}</dd>
                    <dt className={label}>Base URL</dt>
                    <dd className="text-[#111827] font-mono text-[11px] break-all">{status.base_url}</dd>
                  </dl>
                  {!status.configured && (
                    <div className="rounded-md bg-[#FFF9DB] border border-[#FCE996] p-3 text-[12px] text-[#7A5B00]"
                      data-testid="abdm-creds-hint">
                      Add <code className="font-mono">ABDM_CLIENT_ID</code> and
                      {" "}<code className="font-mono">ABDM_CLIENT_SECRET</code> to the backend
                      environment. No code change needed — everything else is wired to the live sandbox.
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className={`${card} p-5`}>
              <p className="font-heading font-bold text-[#111827]">M1 scope in LabTrack</p>
              <ul className="mt-3 space-y-2" data-testid="abdm-m1-checklist">
                {M1_CHECKLIST.map((c) => (
                  <li key={c} className="flex items-start gap-2 text-sm text-[#374151]">
                    <CheckCircle2 className="h-4 w-4 text-[#2E7D32] mt-0.5 shrink-0" /> {c}
                  </li>
                ))}
              </ul>
              <div className="mt-4 flex gap-2">
                <Button onClick={() => setTab("create")} data-testid="abdm-cta-create"
                  className="bg-[#002FA7] hover:bg-[#00237C] text-white">
                  <IdCard className="h-4 w-4 mr-2" /> Create ABHA</Button>
                <Button variant="outline" onClick={() => setTab("verify")} data-testid="abdm-cta-verify">
                  <UserCheck className="h-4 w-4 mr-2" /> Verify existing</Button>
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="create" className="mt-4">
          <div className={`${card} p-5 max-w-2xl`}>
            <p className="font-heading font-bold text-[#111827] mb-1">Create ABHA via Aadhaar OTP</p>
            <p className="text-xs text-[#6B7280] mb-4">
              Collect consent, verify Aadhaar with a UIDAI OTP, then pick an ABHA address.</p>
            <CreateAbha consentText={consentText} onDone={afterAction} />
          </div>
        </TabsContent>

        <TabsContent value="verify" className="mt-4">
          <div className={`${card} p-5 max-w-2xl`}>
            <p className="font-heading font-bold text-[#111827] mb-1">Verify & link an existing ABHA</p>
            <p className="text-xs text-[#6B7280] mb-4">
              For patients who already have an ABHA — verify by Aadhaar, ABHA number or mobile OTP.</p>
            <VerifyAbha consentText={consentText} onDone={afterAction} />
          </div>
        </TabsContent>

        <TabsContent value="links" className="mt-4">
          <LinkedPatients reloadKey={reloadKey} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
